/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Settings › Governance, rebuilt on the kit (overhaul step 4): persona and house rules, the feature flags, connectors,
 * outbound webhooks, the DLP policy, sensitivity labels, the Office add-in's switches, Create my PPP and the right to
 * erasure. The desktop mascot's outfits moved to Outfits and mascot › Gallery (screens/Outfits.tsx). The same calls and the
 * same save per panel as before; the OAuth connectors card joined the kit in step 5 (a table of connector, status and
 * the connect or disconnect action, with Refresh status in the panel head).
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Flag, Info, MessageSquare, Plug, RefreshCw, ShieldOff, Trash2, UserX, Webhook as WebhookIcon } from 'lucide-react';
import { api, ApiError, type Webhook, type FeatureFlag } from '../lib/api';
import { clampPercent, errorText, PERSONA_LIMITS } from '../lib/errors';
import { backgroundErasureFamilyText, backgroundErasureSubmission, backgroundErasureText, eraseErrorText, eraseResultText, flagsReadErrorText, genericFlagEntries, type BackgroundErasureResult, type BackgroundErasureReview } from '../lib/governance';
import { LabelPolicyCard } from '../components/LabelPolicyCard';
import { OfficeCard } from '../components/OfficeCard';
import { PppCard } from '../components/PppCard';
import { StarscribeCard } from '../components/StarscribeCard';
import { GovernanceCopyCard } from '../components/GovernanceCopyCard';
import { SaveNote } from '../components/SaveNote';
import { PPP_FLAG_HELP } from '../lib/ppp';
import {
  Button, Callout, ConfirmDialog, EmptyState, Field, Grid, Icon, Input, NumberInput, PageHeader, Panel, Skeleton, Stack, StatusChip, Switch, Table, Tag, Textarea,
  type Column,
} from '../ui';

/** What the flags gyro-svc reads by name do. `code-execution` is listed even before anyone stores it: it defaults to on. */
const FLAG_HELP: Record<string, string> = {
  'code-execution': "Code execution (default on, 100%). Gyro runs Python in Anthropic's code sandbox: on attached files in every app (the original file is uploaded), and on every turn in the desktop app and the Office pane, where with nothing attached only images (infographics, charts) are offered for download. Switch off for no sandbox anywhere (attached files are answered from the text preview); applies from the next message. Roles cannot be used: stage with percent.",
  'image-generation': "Pictures on request (default on, 100%). Gyro makes a picture with OpenAI's image model when someone asks for one (an illustration, a logo mock-up, a rendering, a poster, an icon), on every chat model, and offers it under the answer as a PNG; the spend counts against the usual app and person caps. Needs the OpenAI key (Settings > Model & key); without it the tool is absent. Switch off for no pictures anywhere; applies from the next message. Roles cannot be used: stage with percent.",
  'desktop-m365-read': "Desktop reads of the user's own Microsoft 365 mail, chats and calendar on their PC. Roles cannot be used: stage with percent.",
  'desktop-mcp-read': "Inline local MCP reads on the desktop app (mcp_list_tools and the reviewed Bluebeam Revu reads through mcp_read) for agents that declare mcp.read.v1. Off until stored on; every other MCP call still needs the person's confirmation. Roles cannot be used: stage with percent.",
  'ppp-report': PPP_FLAG_HELP,
  briefing: "Morning briefing (default off). A person who opts in from the desktop Settings gets a signed push at the hour they chose; the desktop then asks Gyro \"What is waiting on me?\" with the person's own sign-in, and nothing is read until that turn runs on their PC. Roles cannot be used: stage with percent.",
};

/** One row of the OAuth vault: what /v1/connectors answers per connector. */
type Connector = { id: string; displayName: string; configured: boolean; connected: boolean };

export function Governance() {
  // Persona
  const [persona, setPersona] = useState<{ name?: string; greeting?: string; houseRules?: string }>({});
  const [savingPersona, setSavingPersona] = useState(false);
  const [personaNote, setPersonaNote] = useState<{ ok: boolean; text: string } | null>(null);
  // Flags: one copy for the generic table and the Office card, so a save in either shows in both.
  const [flags, setFlags] = useState<Record<string, FeatureFlag>>({});
  const [flagsError, setFlagsError] = useState<string | null>(null);
  const [newFlag, setNewFlag] = useState('');
  const [flagNote, setFlagNote] = useState<{ ok: boolean; text: string } | null>(null);
  // Webhooks
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [webhooksError, setWebhooksError] = useState<string | null>(null);
  const [whUrl, setWhUrl] = useState('');
  const [whSecret, setWhSecret] = useState('');
  const [whEvents, setWhEvents] = useState('*');
  const [whError, setWhError] = useState<string | null>(null);
  // Redaction
  const [denyTerms, setDenyTerms] = useState('');
  const [savingDlp, setSavingDlp] = useState(false);
  const [dlpNote, setDlpNote] = useState<{ ok: boolean; text: string } | null>(null);
  // Erase
  const [eraseId, setEraseId] = useState('');
  const [eraseResult, setEraseResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [eraseReviewRequired, setEraseReviewRequired] = useState<boolean | null>(null);
  const [eraseReview, setEraseReview] = useState<{ target: string; value: BackgroundErasureReview } | null>(null);
  const [eraseBusy, setEraseBusy] = useState(false);
  const [eraseConfirm, setEraseConfirm] = useState<'reviewed' | 'other-only' | null>(null);
  const eraseInFlight = useRef(false);
  const [eraseSubmission, setEraseSubmission] = useState<{ target: string; request: NonNullable<Parameters<typeof api.erase>[1]> } | null>(null);
  // Connectors
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const eraseCapabilities = api.eraseCapabilities().then(value => setEraseReviewRequired(value.reviewRequired), () => {
      setEraseReviewRequired(null);
    });
    // The flags are read on their own, so a failure is shown on both flag views instead of defaults that look stored.
    const flagsRead = api.flags().then(
      (f) => {
        setFlags(f.flags ?? {});
        setFlagsError(null);
      },
      (e: unknown) => {
        setFlags({});
        setFlagsError(flagsReadErrorText(e));
      },
    );
    try {
      // The webhook list loads on its own: when the service cannot read it (503 webhooks_unavailable) its card says so,
      // and the persona and DLP policy still load instead of the whole screen staying empty (the flags load on their
      // own above).
      const webhookList = api.webhooks().then(
        (r) => ({ webhooks: r.webhooks ?? [], error: null }),
        (e: unknown) => ({ webhooks: [] as Webhook[], error: errorText(e, 'The webhook list could not be loaded.') }),
      );
      const [p, w, r, c] = await Promise.all([api.persona(), webhookList, api.redaction(), api.connectors().catch(() => ({ connectors: [] }))]);
      setPersona(p.persona ?? {});
      setWebhooks(w.webhooks);
      setWebhooksError(w.error);
      setDenyTerms((r.policy.denyTerms ?? []).join(', '));
      setConnectors(c.connectors ?? []);
    } finally {
      await Promise.all([flagsRead, eraseCapabilities]);
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const savePersona = async () => {
    setSavingPersona(true);
    setPersonaNote(null);
    try {
      await api.savePersona(persona);
      setPersonaNote({ ok: true, text: 'Persona saved.' });
    } catch (e) {
      setPersonaNote({ ok: false, text: errorText(e, 'The persona was not saved.') });
    } finally {
      setSavingPersona(false);
    }
  };

  const setFlagValue = async (key: string, flag: FeatureFlag) => {
    setFlagNote(null);
    try {
      const r = await api.setFlag(key, flag);
      setFlags(r.flags);
    } catch (e) {
      setFlagNote({ ok: false, text: `${key}: ${errorText(e, 'the flag was not saved.')}` });
    }
  };

  const addWebhook = async () => {
    if (!whUrl) return;
    setWhError(null);
    try {
      const r = await api.addWebhook({ url: whUrl, secret: whSecret || undefined, events: whEvents.split(',').map((s) => s.trim()).filter(Boolean) });
      setWebhooks(r.webhooks);
      setWebhooksError(null);
      setWhUrl('');
      setWhSecret('');
    } catch (e) {
      // e.g. notification_channel_forbidden: the service explains that Teams/Slack/Workflows go through the Bridge.
      setWhError(e instanceof Error ? e.message : 'Could not add the webhook.');
    }
  };

  const saveDlp = async () => {
    setSavingDlp(true);
    setDlpNote(null);
    try {
      await api.setRedaction({ denyTerms: denyTerms.split(',').map((s) => s.trim()).filter(Boolean) });
      setDlpNote({ ok: true, text: 'Policy saved.' });
    } catch (e) {
      setDlpNote({ ok: false, text: errorText(e, 'The policy was not saved.') });
    } finally {
      setSavingDlp(false);
    }
  };

  const changeEraseId = (value: string) => {
    if (eraseInFlight.current) return;
    setEraseId(value); setEraseReview(null); setEraseResult(null); setEraseConfirm(null); setEraseSubmission(null);
  };
  const eraseLegacy = async () => {
    if (!eraseId || eraseReviewRequired !== false || eraseInFlight.current) return;
    eraseInFlight.current = true; setEraseBusy(true); setEraseResult(null);
    try {
      const r = await api.erase(eraseId);
      setEraseResult({ ok: r.attentionErasure !== 'incomplete' && !r.background, text: eraseResultText(r, eraseId) });
      setEraseId('');
    } catch (e) { setEraseResult({ ok: false, text: eraseErrorText(e) }); }
    finally { eraseInFlight.current = false; setEraseBusy(false); }
  };
  const reviewErase = async () => {
    if (!eraseId.trim() || eraseInFlight.current) return;
    const target = eraseId;
    eraseInFlight.current = true; setEraseBusy(true);
    try { setEraseReview({ target, value: await api.reviewBackgroundErasure(target) }); }
    catch (e) {
      setEraseReview({ target, value: { state: 'unavailable', accountDeletionConfirmed: false,
        message: errorText(e, 'Background task data could not be reviewed. No cleanup was requested.') } });
    } finally { eraseInFlight.current = false; setEraseBusy(false); }
  };
  const erase = async () => {
    if (!eraseConfirm || eraseInFlight.current || !eraseReview || eraseReview.target !== eraseId) return;
    const background = backgroundErasureSubmission(eraseReview.value);
    if (!eraseSubmission && eraseConfirm === 'reviewed' && !background) return;
    // Keep the original target, reviewed hash and key across uncertain responses.
    const submission = eraseSubmission ?? { target: eraseReview.target,
      request: { requestId: crypto.randomUUID(), ...(eraseConfirm === 'reviewed' && background ? { background } : {}) } };
    setEraseSubmission(submission);
    eraseInFlight.current = true; setEraseBusy(true);
    setEraseResult(null);
    try {
      const r = await api.erase(submission.target, submission.request);
      setEraseResult({ ok: false, text: `${eraseResultText(r, submission.target)} ${backgroundErasureText(r.background)}` });
      if (r.background?.progress) setEraseReview(current => current && ({ ...current, value: { ...current.value, progress: r.background!.progress } }));
      setEraseConfirm(null);
    } catch (e) {
      const detail = e instanceof ApiError ? e.body?.background as BackgroundErasureResult | undefined : undefined;
      setEraseResult({ ok: false, text: eraseErrorText(e, 'The result could not be confirmed. Some cleanup may have started; retry this same request or refresh its progress.') + (detail ? ` ${backgroundErasureText(detail)}` : '') });
      if (detail?.progress) setEraseReview(current => current && ({ ...current, value: { ...current.value, progress: detail.progress } }));
      // A definite refusal before admission may be reviewed afresh. A lost
      // response or later-store failure must retain its original request.
      if (e instanceof ApiError && e.status === 409 && detail?.state === 'not-requested' && e.body?.error === 'owner_erasure_review_changed') {
        setEraseSubmission(null); setEraseReview(null);
      }
      setEraseConfirm(null);
    } finally { eraseInFlight.current = false; setEraseBusy(false); }
  };

  if (loading) {
    return (
      <>
        <PageHeader meta={<Skeleton width="200px" />} />
        <div className="ui-page" aria-busy="true">
          <Grid cols={2}>
            <Panel title="Persona & house rules" description="How Gyro presents himself"><div className="ui-form" aria-hidden="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="xl" /></div></Panel>
            <Panel title="Feature flags" description="Capability rollout, by percent of people"><div className="ui-form" aria-hidden="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="lg" /></div></Panel>
          </Grid>
        </div>
      </>
    );
  }

  const generic = genericFlagEntries(flags);
  const webhookColumns: Array<Column<Webhook>> = [
    { key: 'url', header: 'URL', mono: true, cell: (w) => w.url },
    { key: 'events', header: 'Events', muted: true, cell: (w) => w.events.join(', ') },
    {
      key: 'note', header: 'Note', muted: true, hideOnPhone: true,
      cell: (w) => (w.forbiddenChannel ? `Registered before the Bridge rule (${w.forbiddenChannel}); migrate to The Bridge → Star Command.` : '—'),
    },
    {
      key: 'remove', header: <span className="ui-sr">Remove</span>, align: 'end', width: '56px',
      cell: (w) => (
        <Button
          variant="ghost"
          size="sm"
          icon={Trash2}
          aria-label={`Remove webhook ${w.url}`}
          title="Remove"
          onClick={() => void api.deleteWebhook(w.id).then((r) => { setWebhooks(r.webhooks); setWebhooksError(null); }, (e: unknown) => setWhError(errorText(e, 'The webhook was not removed.')))}
        />
      ),
    },
  ];

  const connectorColumns: Array<Column<Connector>> = [
    { key: 'name', header: 'Connector', cell: (c) => c.displayName },
    {
      key: 'status', header: 'Status',
      cell: (c) => (c.configured
        ? (c.connected ? <StatusChip tone="ok">Connected</StatusChip> : <StatusChip tone="neutral">Not linked</StatusChip>)
        : <StatusChip tone="warn">Not configured</StatusChip>),
    },
    {
      key: 'link', header: <span className="ui-sr">Connect or disconnect</span>, align: 'end', width: '140px',
      cell: (c) => (c.configured ? (
        <span className="ui-row-actions">
          {c.connected
            ? <Button variant="ghost" size="sm" onClick={() => void api.revokeConnector(c.id).then(load)}>Disconnect</Button>
            : <Button size="sm" onClick={() => { window.open(`/oauth/${c.id}/connect`, 'gyro-connect', 'width=520,height=640'); }}>Connect</Button>}
        </span>
      ) : null),
    },
  ];

  return (
    <>
      <PageHeader meta={<span>Persona, flags, connectors, webhooks, data protection, personal reports and erasure: all central, all audited. The mascot&apos;s outfits are under Outfits and mascot › Gallery.</span>} />
      <div className="ui-page">
        <Stack>
          <Grid cols={2}>
            <Panel
              title={<span className="ui-title-icon"><Icon icon={MessageSquare} size="md" />Persona &amp; house rules</span>}
              description="How Gyro presents himself, and the guidance added to every system prompt"
              footer={<><Icon icon={Info} size="sm" /><span>Safety clauses (no claiming writes ran, restricted-data rules) are non-overridable; the house rules only add guidance.</span></>}
            >
              <div className="ui-form">
                <Field label="Assistant name">
                  <Input maxLength={PERSONA_LIMITS.name} value={persona.name ?? ''} onChange={(e) => setPersona((p) => ({ ...p, name: e.target.value }))} placeholder="Gyro" />
                </Field>
                <Field label="Greeting">
                  <Input maxLength={PERSONA_LIMITS.greeting} value={persona.greeting ?? ''} onChange={(e) => setPersona((p) => ({ ...p, greeting: e.target.value }))} placeholder="Hi — I'm Gyro…" />
                </Field>
                <Field
                  label="House rules (appended to the system prompt)"
                  help={`${(persona.houseRules ?? '').length.toLocaleString()} / ${PERSONA_LIMITS.houseRules.toLocaleString()} characters.`}
                >
                  <Textarea rows={4} maxLength={PERSONA_LIMITS.houseRules} value={persona.houseRules ?? ''} onChange={(e) => setPersona((p) => ({ ...p, houseRules: e.target.value }))} placeholder="e.g. Always cite the project number when discussing Deltek." />
                </Field>
                <div className="ui-actions">
                  <Button variant="primary" icon={Check} busy={savingPersona} onClick={() => void savePersona()}>Save persona</Button>
                </div>
                <SaveNote note={personaNote} />
              </div>
            </Panel>

            <Panel
              flush
              title={<span className="ui-title-icon"><Icon icon={Flag} size="md" />Feature flags</span>}
              description="Capability rollout, by percent of people"
              footer={<><Icon icon={Info} size="sm" /><span>The Office add-in&apos;s switches and desktop-m365-reach are on the Office add-in panel, with their defaults.</span></>}
            >
              {flagsError ? (
                <div className="ui-panel-pad"><Callout tone="bad" role="alert">{flagsError}</Callout></div>
              ) : generic.length === 0 ? (
                <EmptyState compact icon={Flag} title="No flags yet">Add a flag below to stage a capability by percent of people.</EmptyState>
              ) : (
                <table className="ui-table ui-table--tall is-stacked" aria-label="Feature flags">
                  <thead>
                    <tr><th scope="col">Flag</th><th scope="col">State</th><th scope="col" className="is-end">Rollout</th></tr>
                  </thead>
                  <tbody>
                    {generic.map(([k, f]) => (
                      <tr key={k}>
                        <td data-th="Flag">
                          <div className="ui-cell-stack">
                            <span><Tag>{k}</Tag></span>
                            {FLAG_HELP[k] && <span className="ui-cell-sub">{FLAG_HELP[k]}</span>}
                          </div>
                        </td>
                        <td data-th="State">
                          <Switch checked={f.enabled} onChange={(on) => void setFlagValue(k, { ...f, enabled: on })} label={`${k} switched on`} labelHidden stateLabels={['on', 'off']} />
                        </td>
                        <td className="is-end" data-th="Rollout">
                          <NumberInput
                            size="sm"
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            suffix="%"
                            className="ui-pct"
                            aria-label={`${k} rollout percent`}
                            value={f.percent ?? 100}
                            onChange={(e) => void setFlagValue(k, { ...f, percent: clampPercent(Number(e.target.value)) })}
                            title="rollout % (0 to 100)"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="ui-panel-pad ui-panel-pad--top">
                <div className="ui-form">
                  <div className="ui-actions">
                    <Input placeholder="new-flag-key" aria-label="New flag key" value={newFlag} onChange={(e) => setNewFlag(e.target.value)} className="ui-grow" />
                    <Button disabled={!newFlag} onClick={() => { void setFlagValue(newFlag, { enabled: false, percent: 100 }); setNewFlag(''); }}>Add</Button>
                  </div>
                  <SaveNote note={flagNote} />
                </div>
              </div>
            </Panel>

            <Panel
              flush
              title={<span className="ui-title-icon"><Icon icon={Plug} size="md" />Connectors (OAuth vault)</span>}
              description="Link a SaaS account to Gyro"
              actions={<Button variant="ghost" size="sm" icon={RefreshCw} onClick={() => void load()}>Refresh status</Button>}
              footer={(
                <>
                  <Icon icon={Info} size="sm" />
                  <span className="ui-foot-text">
                    <span className="ui-para">Tokens are encrypted at rest; the model never sees them. &quot;Not configured&quot; means the admin env (client id/secret/redirect) isn&apos;t set yet.</span>
                    <span className="ui-para">After connecting in the popup, click Refresh status.</span>
                  </span>
                </>
              )}
            >
              <Table
                stacked
                label="Connectors"
                columns={connectorColumns}
                rows={connectors}
                rowKey={(c) => c.id}
                stickyHeader={false}
                empty={<EmptyState compact icon={Plug} title="No connectors registered">A connector appears here once gyro-svc registers it.</EmptyState>}
              />
            </Panel>

            <Panel
              flush
              title={<span className="ui-title-icon"><Icon icon={WebhookIcon} size="md" />Outbound webhooks</span>}
              description="Action and spend events pushed to an ERP or automation endpoint"
              footer={<><Icon icon={Info} size="sm" /><span>Notifications to people (Teams, Slack, email) go through The Bridge to Star Command, not a webhook.</span></>}
            >
              {webhooksError
                ? <div className="ui-panel-pad"><Callout tone="bad" role="alert">{webhooksError}</Callout></div>
                : webhooks.length === 0
                  ? <EmptyState compact icon={WebhookIcon} title="No webhooks yet">Add one to push action and spend events to an ERP or automation endpoint.</EmptyState>
                  : <Table stacked label="Outbound webhooks" columns={webhookColumns} rows={webhooks} rowKey={(w) => w.id} stickyHeader={false} />}
              <div className="ui-panel-pad ui-panel-pad--top">
                <div className="ui-form">
                  <Field label="URL">
                    <Input value={whUrl} onChange={(e) => setWhUrl(e.target.value)} placeholder="https://hooks.example.com/gyro" />
                  </Field>
                  <Grid cols={2}>
                    <Field label="HMAC secret (optional)">
                      <Input value={whSecret} onChange={(e) => setWhSecret(e.target.value)} placeholder="HMAC secret (optional)" />
                    </Field>
                    <Field label="Events" help="* for every event, or a comma-separated list">
                      <Input value={whEvents} onChange={(e) => setWhEvents(e.target.value)} placeholder="* or action.confirmed,usage.threshold" />
                    </Field>
                  </Grid>
                  <div className="ui-actions">
                    <Button variant="primary" icon={Check} disabled={!whUrl} onClick={() => void addWebhook()}>Add webhook</Button>
                  </div>
                  {whError && <Callout tone="bad" role="alert">{whError}</Callout>}
                </div>
              </div>
            </Panel>

            <Panel
              title={<span className="ui-title-icon"><Icon icon={ShieldOff} size="md" />DLP redaction policy</span>}
              description="Terms always redacted before the model sees them"
            >
              <div className="ui-form">
                <Field label="Deny terms (always redacted, comma-separated)" help="In addition to the built-in detectors (keys, SSNs, cards, secrets).">
                  <Input value={denyTerms} onChange={(e) => setDenyTerms(e.target.value)} placeholder="Project Falcon, acquisition" />
                </Field>
                <div className="ui-actions">
                  <Button variant="primary" icon={Check} busy={savingDlp} onClick={() => void saveDlp()}>Save policy</Button>
                </div>
                <SaveNote note={dlpNote} />
              </div>
            </Panel>

            <LabelPolicyCard />

            <OfficeCard flags={flags} flagsError={flagsError} onFlagsSaved={setFlags} />

            <PppCard />

            <StarscribeCard />

            <GovernanceCopyCard />

            <Panel
              title={<span className="ui-title-icon"><Icon icon={UserX} size="md" />Right to erasure</span>}
              description="Erase what Gyro stores for one person"
            >
              <div className="ui-form">
                {eraseReviewRequired === null && <Callout tone="warn">Cleanup requirements could not be checked. Refresh before erasing stored data.</Callout>}
                <Field label="User id" help={eraseReviewRequired === true ? "Use the person's Entra object id in your organization to review background task data. An email can identify other stored data, but cannot authorize background cleanup." : "Outfit picks and star ratings are found by the person's Entra object id; an email finds them only when Gyro knows which object id it belongs to."}>
                  <Input value={eraseId} disabled={eraseBusy || eraseConfirm !== null} onChange={(e) => changeEraseId(e.target.value)} placeholder={eraseReviewRequired === true ? 'Entra object id or legacy user id' : 'user id'} />
                </Field>
                <div className="ui-actions">
                  {eraseReviewRequired === true
                    ? <Button icon={RefreshCw} busy={eraseBusy} disabled={!eraseId.trim()} onClick={() => void reviewErase()}>{eraseReview ? 'Refresh cleanup review' : 'Review stored data'}</Button>
                    : <Button variant="danger" icon={Trash2} busy={eraseBusy} disabled={!eraseId || eraseReviewRequired !== false} onClick={() => void eraseLegacy()}>Erase stored conversations</Button>}
                </div>
                {eraseReviewRequired === true && eraseReview && eraseReview.target === eraseId && (
                  <Stack>
                    <Callout tone={eraseReview.value.state === 'reviewed' ? 'info' : 'warn'} title="Background task data" role="status">
                      <p>{eraseReview.value.message}</p>
                      {eraseReview.value.subjectId && <p>Reviewed person: <strong>{eraseReview.value.subjectId}</strong></p>}
                      {eraseReview.value.counts && <p>{eraseReview.value.counts.memory} saved notes · {eraseReview.value.counts.contexts} context snapshots · {eraseReview.value.counts.instructions} instructions</p>}
                      {eraseReview.value.protectedOperations && <p>{eraseReview.value.protectedOperations.unfinishedRuns} unfinished operations · {eraseReview.value.protectedOperations.unresolvedRuns} unresolved original operations</p>}
                    </Callout>
                    <Callout tone="warn" title="A staged cleanup, not account deletion">
                      <p>Confirming background cleanup stops this person's background work. Notes and context copies are cleared in batches. Device copies, connections, protected operation records and other retained data still require follow-through.</p>
                      <p>Existing charges and unresolved operations remain protected. Neither this review nor a completed batch confirms full erasure or deletes the person's account.</p>
                    </Callout>
                    {eraseReview.value.progress && <Callout tone="info" title="Cleanup progress" role="status">
                      <p><StatusChip tone="warn">Full erasure pending</StatusChip></p>
                      <p>Notes and context copies: {eraseReview.value.progress.memoryContext.state === 'completed' ? 'batch cleanup finished' : 'cleanup pending'} · {eraseReview.value.progress.memoryContext.rowsRedacted} rows redacted.</p>
                      <p>Requested {eraseReview.value.progress.requestedAt} · Request {eraseReview.value.progress.requestId}</p>
                      <ul>{eraseReview.value.progress.pendingFamilies.map((family, index) => <li key={`${family}:${index}`}>{backgroundErasureFamilyText(family)}</li>)}</ul>
                    </Callout>}
                    <div className="ui-actions">
                      {eraseSubmission ? <Button variant="danger" icon={Trash2} disabled={eraseBusy} onClick={() => setEraseConfirm(eraseSubmission?.request.background ? 'reviewed' : 'other-only')}>Retry original cleanup request</Button>
                        : backgroundErasureSubmission(eraseReview.value) ? <Button variant="danger" icon={Trash2} disabled={eraseBusy} onClick={() => setEraseConfirm('reviewed')}>Review and confirm cleanup</Button>
                          : <Button variant="danger" icon={Trash2} disabled={eraseBusy} onClick={() => setEraseConfirm('other-only')}>Erase other stored data only</Button>}
                    </div>
                    {!backgroundErasureSubmission(eraseReview.value) && <p>Background task cleanup remains unresolved. Erasing other stored data will not admit it.</p>}
                  </Stack>
                )}
                <SaveNote note={eraseResult} />
              </div>
            </Panel>
          </Grid>
        </Stack>
      </div>
      <ConfirmDialog open={eraseConfirm !== null} title={eraseConfirm === 'reviewed' ? 'Confirm staged data cleanup?' : 'Erase other stored data only?'}
        confirmLabel={eraseSubmission ? 'Retry original request' : 'Confirm cleanup'} busy={eraseBusy}
        onConfirm={() => void erase()} onCancel={() => { if (!eraseInFlight.current) setEraseConfirm(null); }}>
        <p>Person: <strong>{eraseSubmission?.target ?? eraseReview?.target}</strong></p>
        <p>{(eraseSubmission ? Boolean(eraseSubmission.request.background) : eraseConfirm === 'reviewed')
          ? 'This stops the reviewed background work and requests bounded cleanup, then erases the other stored data. Full erasure remains pending.'
          : 'This erases the other stored data only. Background task data has not been approved for cleanup and remains unresolved.'}</p>
        <p>This cannot be undone. It does not delete the person's account.</p>
      </ConfirmDialog>
    </>
  );
}
